import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials (cookies) with every request
axios.defaults.withCredentials = true;

export function useAppData() {
  const [dayCache, setDayCache] = useState({});
  const [version, setVersion] = useState(0);
  const loadingRef = useRef({});

  const updateDayInCache = useCallback((day) => {
    if (day && day.date) {
      setDayCache(prev => ({ ...prev, [day.date]: day }));
      setVersion(v => v + 1);
    }
  }, []);

  const invalidateMonthlyCache = useCallback((dateKey) => {
    // When data changes, invalidate monthly and historical caches
    const [year, month] = dateKey.split('-');
    const m = parseInt(month) - 1; // Convert to 0-indexed
    const expKey = `expenses_${year}_${m}`;
    const goalKey = `goals_${year}_${m}`;
    setDayCache(prev => {
      const next = { ...prev };
      delete next[expKey];
      delete next[goalKey];
      delete next['_all_goals'];
      delete next['_all_expenses'];
      return next;
    });
    // Reset loading refs so they can be re-fetched
    delete loadingRef.current[expKey];
    delete loadingRef.current[goalKey];
    delete loadingRef.current['_all_goals'];
    delete loadingRef.current['_all_expenses'];
  }, []);

  const getDayData = useCallback((dateKey) => {
    if (dayCache[dateKey]) {
      return dayCache[dateKey];
    }

    if (!loadingRef.current[dateKey]) {
      loadingRef.current[dateKey] = true;
      axios.get(`${API}/days/${dateKey}`)
        .then(res => {
          updateDayInCache(res.data);
          loadingRef.current[dateKey] = false;
        })
        .catch(err => {
          console.error('Error fetching day:', dateKey, err);
          loadingRef.current[dateKey] = false;
        });
    }

    return {
      date: dateKey,
      todos: [],
      notes: [],
      expenses: [],
      goals: [],
    };
  }, [dayCache, updateDayInCache]);

  // ==================== TODOS ====================

  const addTodo = useCallback(async (dateKey, text, deadline) => {
    try {
      const res = await axios.post(`${API}/days/${dateKey}/todos`, { text, deadline: deadline || null });
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error adding todo:', err);
    }
  }, [updateDayInCache]);

  const toggleTodo = useCallback(async (dateKey, todoId) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/todos/${todoId}/toggle`);
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error toggling todo:', err);
    }
  }, [updateDayInCache]);

  const deleteTodo = useCallback(async (dateKey, todoId) => {
    try {
      const res = await axios.delete(`${API}/days/${dateKey}/todos/${todoId}`);
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error deleting todo:', err);
    }
  }, [updateDayInCache]);

  const setTodoDeadline = useCallback(async (dateKey, todoId, deadline) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/todos/${todoId}/deadline`, { deadline });
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error setting todo deadline:', err);
    }
  }, [updateDayInCache]);

  // ==================== NOTES (BITÁCORA) ====================

  const addNote = useCallback(async (dateKey, text) => {
    try {
      const res = await axios.post(`${API}/days/${dateKey}/notes`, { text });
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error adding note:', err);
    }
  }, [updateDayInCache]);

  const updateNote = useCallback(async (dateKey, noteId, text) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/notes/${noteId}`, { text });
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error updating note:', err);
    }
  }, [updateDayInCache]);

  const deleteNote = useCallback(async (dateKey, noteId) => {
    try {
      const res = await axios.delete(`${API}/days/${dateKey}/notes/${noteId}`);
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  }, [updateDayInCache]);

  // ==================== EXPENSES ====================

  const addExpense = useCallback(async (dateKey, amount, concepto, projectId) => {
    try {
      const res = await axios.post(`${API}/days/${dateKey}/expenses`, {
        description: 'Gasto',
        amount: parseFloat(amount),
        category: 'General',
        concepto: concepto || '',
        project_id: projectId || null
      });
      updateDayInCache(res.data);
      invalidateMonthlyCache(dateKey);
    } catch (err) {
      console.error('Error adding expense:', err);
    }
  }, [updateDayInCache, invalidateMonthlyCache]);

  const deleteExpense = useCallback(async (dateKey, expenseId) => {
    try {
      const res = await axios.delete(`${API}/days/${dateKey}/expenses/${expenseId}`);
      updateDayInCache(res.data);
      invalidateMonthlyCache(dateKey);
    } catch (err) {
      console.error('Error deleting expense:', err);
    }
  }, [updateDayInCache, invalidateMonthlyCache]);

  // ==================== GOALS ====================

  const addGoal = useCallback(async (dateKey, title, target, unit, deadline) => {
    try {
      const res = await axios.post(`${API}/days/${dateKey}/goals`, {
        title,
        target: parseFloat(target) || 100,
        unit: unit || '%',
        deadline: deadline || null
      });
      updateDayInCache(res.data);
      invalidateMonthlyCache(dateKey);
    } catch (err) {
      console.error('Error adding goal:', err);
    }
  }, [updateDayInCache, invalidateMonthlyCache]);

  const updateGoalProgress = useCallback(async (dateKey, goalId, progress) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/goals/${goalId}/progress`, {
        progress: parseFloat(progress)
      });
      updateDayInCache(res.data);
      invalidateMonthlyCache(dateKey);
    } catch (err) {
      console.error('Error updating goal progress:', err);
    }
  }, [updateDayInCache, invalidateMonthlyCache]);

  const setGoalProgressAbsolute = useCallback(async (dateKey, goalId, progress) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/goals/${goalId}/progress/set`, {
        progress: parseFloat(progress)
      });
      updateDayInCache(res.data);
      invalidateMonthlyCache(dateKey);
      // Also invalidate the all goals cache so the historical view updates
      setDayCache(prev => {
        const next = { ...prev };
        delete next['_all_goals'];
        return next;
      });
      delete loadingRef.current['_all_goals'];
      return { success: true };
    } catch (err) {
      console.error('Error setting goal progress:', err);
      return { success: false, error: err.message };
    }
  }, [updateDayInCache, invalidateMonthlyCache]);

  const setGoalDeadline = useCallback(async (dateKey, goalId, deadline) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/goals/${goalId}/deadline`, { deadline });
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error setting goal deadline:', err);
    }
  }, [updateDayInCache]);

  const updateGoalTitle = useCallback(async (dateKey, goalId, title) => {
    try {
      const res = await axios.put(`${API}/days/${dateKey}/goals/${goalId}/title`, { title });
      updateDayInCache(res.data);
    } catch (err) {
      console.error('Error updating goal title:', err);
    }
  }, [updateDayInCache]);

  const fixText = useCallback(async (text) => {
    const res = await axios.post(`${API}/ai/fix-text`, { text });
    return res.data;
  }, []);

  const deleteGoal = useCallback(async (dateKey, goalId) => {
    try {
      const res = await axios.delete(`${API}/days/${dateKey}/goals/${goalId}`);
      updateDayInCache(res.data);
      invalidateMonthlyCache(dateKey);
    } catch (err) {
      console.error('Error deleting goal:', err);
    }
  }, [updateDayInCache, invalidateMonthlyCache]);

  // ==================== MONTHLY ====================

  const getMonthlyExpenses = useCallback((year, month) => {
    const cacheKey = `expenses_${year}_${month}`;
    if (dayCache[cacheKey]) {
      return dayCache[cacheKey];
    }

    if (!loadingRef.current[cacheKey]) {
      loadingRef.current[cacheKey] = true;
      axios.get(`${API}/monthly/expenses/${year}/${month}`)
        .then(res => {
          setDayCache(prev => ({ ...prev, [cacheKey]: res.data }));
          setVersion(v => v + 1);
          loadingRef.current[cacheKey] = false;
        })
        .catch(err => {
          console.error('Error fetching monthly expenses:', err);
          loadingRef.current[cacheKey] = false;
        });
    }

    return { categories: [], daily: [], total: 0, year, month };
  }, [dayCache]);

  const getMonthlyTotal = useCallback((year, month) => {
    const cacheKey = `expenses_${year}_${month}`;
    if (dayCache[cacheKey]) {
      return dayCache[cacheKey].total || 0;
    }
    return 0;
  }, [dayCache]);

  const getMonthlyGoals = useCallback((year, month) => {
    const cacheKey = `goals_${year}_${month}`;
    if (dayCache[cacheKey]) {
      return dayCache[cacheKey];
    }

    if (!loadingRef.current[cacheKey]) {
      loadingRef.current[cacheKey] = true;
      axios.get(`${API}/monthly/goals/${year}/${month}`)
        .then(res => {
          setDayCache(prev => ({ ...prev, [cacheKey]: res.data }));
          setVersion(v => v + 1);
          loadingRef.current[cacheKey] = false;
        })
        .catch(err => {
          console.error('Error fetching monthly goals:', err);
          loadingRef.current[cacheKey] = false;
        });
    }

    return { goals: [], year, month };
  }, [dayCache]);

  // ==================== ALL HISTORICAL DATA ====================

  const getAllGoals = useCallback(() => {
    const cacheKey = '_all_goals';
    if (dayCache[cacheKey]) return dayCache[cacheKey];
    if (!loadingRef.current[cacheKey]) {
      loadingRef.current[cacheKey] = true;
      axios.get(`${API}/all/goals`)
        .then(res => {
          setDayCache(prev => ({ ...prev, [cacheKey]: res.data }));
          setVersion(v => v + 1);
          loadingRef.current[cacheKey] = false;
        })
        .catch(err => { console.error('Error fetching all goals:', err); loadingRef.current[cacheKey] = false; });
    }
    return { goals: [] };
  }, [dayCache]);

  const getAllExpenses = useCallback(() => {
    const cacheKey = '_all_expenses';
    if (dayCache[cacheKey]) return dayCache[cacheKey];
    if (!loadingRef.current[cacheKey]) {
      loadingRef.current[cacheKey] = true;
      axios.get(`${API}/all/expenses`)
        .then(res => {
          setDayCache(prev => ({ ...prev, [cacheKey]: res.data }));
          setVersion(v => v + 1);
          loadingRef.current[cacheKey] = false;
        })
        .catch(err => { console.error('Error fetching all expenses:', err); loadingRef.current[cacheKey] = false; });
    }
    return { expenses: [], total: 0 };
  }, [dayCache]);

  // ==================== PROJECTS ====================

  const getProjects = useCallback(() => {
    const cacheKey = '_projects';
    if (dayCache[cacheKey]) {
      return dayCache[cacheKey];
    }

    if (!loadingRef.current[cacheKey]) {
      loadingRef.current[cacheKey] = true;
      axios.get(`${API}/projects`)
        .then(res => {
          setDayCache(prev => ({ ...prev, [cacheKey]: res.data }));
          setVersion(v => v + 1);
          loadingRef.current[cacheKey] = false;
        })
        .catch(err => {
          console.error('Error fetching projects:', err);
          loadingRef.current[cacheKey] = false;
        });
    }

    return [];
  }, [dayCache]);

  const createProject = useCallback(async (title, objectives, characteristics, notes, deadline, importance) => {
    try {
      await axios.post(`${API}/projects`, { title, objectives, characteristics, notes, deadline, importance: importance || 'algo' });
      // Invalidate cache
      delete loadingRef.current['_projects'];
      setDayCache(prev => { const n = { ...prev }; delete n['_projects']; return n; });
      setVersion(v => v + 1);
    } catch (err) {
      console.error('Error creating project:', err);
    }
  }, []);

  const updateProject = useCallback(async (projectId, data) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, data);
      delete loadingRef.current['_projects'];
      setDayCache(prev => { const n = { ...prev }; delete n['_projects']; return n; });
      setVersion(v => v + 1);
    } catch (err) {
      console.error('Error updating project:', err);
    }
  }, []);

  const deleteProject = useCallback(async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      delete loadingRef.current['_projects'];
      setDayCache(prev => { const n = { ...prev }; delete n['_projects']; return n; });
      setVersion(v => v + 1);
    } catch (err) {
      console.error('Error deleting project:', err);
    }
  }, []);

  // ==================== AI SUMMARY ====================

  const generateSummary = useCallback(async (mode = 'general') => {
    try {
      const res = await axios.post(`${API}/summary`, { mode });
      return res.data.summary;
    } catch (err) {
      console.error('Error generating summary:', err);
      throw err;
    }
  }, []);

  // ==================== AI SUMMARY CHAT ====================

  const startSummarySession = useCallback(async (mode) => {
    const res = await axios.post(`${API}/summary/start`, { mode });
    return res.data;
  }, []);

  const sendSummaryMessage = useCallback(async (sessionId, message) => {
    const res = await axios.post(`${API}/summary/chat`, { session_id: sessionId, message });
    return res.data;
  }, []);

  // ==================== PROMPTS ====================

  const getPrompts = useCallback(async () => {
    const res = await axios.get(`${API}/prompts`);
    return res.data;
  }, []);

  const savePrompt = useCallback(async (promptType, promptText) => {
    const res = await axios.put(`${API}/prompts/${promptType}`, { prompt_text: promptText });
    return res.data;
  }, []);

  const restorePrompt = useCallback(async (promptType) => {
    const res = await axios.delete(`${API}/prompts/${promptType}`);
    return res.data;
  }, []);

  return {
    getDayData,
    addTodo,
    toggleTodo,
    deleteTodo,
    setTodoDeadline,
    addNote,
    updateNote,
    deleteNote,
    addExpense,
    deleteExpense,
    addGoal,
    updateGoalProgress,
    setGoalProgressAbsolute,
    setGoalDeadline,
    updateGoalTitle,
    fixText,
    deleteGoal,
    getMonthlyExpenses,
    getMonthlyTotal,
    getMonthlyGoals,
    getAllGoals,
    getAllExpenses,
    getProjects,
    createProject,
    updateProject,
    deleteProject,
    generateSummary,
    startSummarySession,
    sendSummaryMessage,
    getPrompts,
    savePrompt,
    restorePrompt,
    version,
  };
}
